import fs from 'node:fs';
import path from 'node:path';
import { pathToFileURL } from 'node:url';
import { inspect } from 'node:util';

import { any as findAny } from 'empathic/find';

import type { EnvironmentResult } from '../environment/index.ts';
import type { Logger } from '../isomorphic/types.ts';
import fetchWithRetry from '../network/fetchWithRetry.ts';
import getShortLivedAPIToken from './getShortLivedAPIToken.ts';
import type {
  ConfigWithDefaults,
  DeepCompareSettings,
  TargetWithDefaults,
} from './index.ts';

const CONFIG_FILENAMES = [
  'happo.config.js',
  'happo.config.mjs',
  'happo.config.cjs',
  'happo.config.ts',
  'happo.config.mts',
  'happo.config.cts',
];

const DEFAULT_ENDPOINT = 'https://happo.io';

export function findConfigFile(): string {
  if (process.env.HAPPO_CONFIG_FILE) {
    return process.env.HAPPO_CONFIG_FILE;
  }

  const configFilePath = findAny(CONFIG_FILENAMES, { cwd: process.cwd() });

  if (!configFilePath) {
    throw new Error(
      'Happo config file could not be found. Please create a config file in the root of your project.',
    );
  }

  return configFilePath;
}

function assertIsPullRequestTokenResponse(
  response: unknown,
): asserts response is { secret: string } {
  if (typeof response !== 'object' || response === null || !('secret' in response)) {
    throw new TypeError('Unexpected pull request token response');
  }
}

async function getPullRequestSecret(
  endpoint: string,
  prUrl: string,
  logger: Logger,
): Promise<string> {
  const url = new URL('/api/pull-request-token', endpoint);
  const res = await fetchWithRetry(
    url,
    {
      method: 'POST',
      body: { prUrl },
      retryCount: 3,
    },
    logger,
  );

  if (!res || !res.ok) {
    throw new Error(
      `Failed to get pull request secret: ${res.status} - ${await res.text()}`,
    );
  }

  const json = await res.json();
  assertIsPullRequestTokenResponse(json);

  return json.secret;
}

async function getFallbackApiToken(
  endpoint: string,
  environment: Pick<EnvironmentResult, 'link' | 'ci'> | undefined,
  logger: Logger,
): Promise<{ key: string; secret: string } | undefined> {
  if (environment?.link) {
    try {
      // Fetch pull request auth
      const pullRequestSecret = await getPullRequestSecret(
        endpoint,
        environment.link,
        logger,
      );
      return {
        key: environment.link,
        secret: pullRequestSecret,
      };
    } catch {
      logger.log(
        `Failed to obtain temporary pull-request token for URL: ${environment.link}`,
      );
    }
  }

  if (!environment?.ci) {
    const shortLivedApiToken = await getShortLivedAPIToken(endpoint, logger);
    return shortLivedApiToken ?? undefined;
  }
  return undefined;
}

function validateDeepCompareSettings(
  deepCompare: DeepCompareSettings,
  configFilePath: string,
): asserts deepCompare is DeepCompareSettings {
  if (typeof deepCompare !== 'object' || Array.isArray(deepCompare)) {
    throw new TypeError(
      `Invalid \`deepCompare\` in config file ${configFilePath}: must be an object, got: ${Array.isArray(deepCompare) ? 'array' : typeof deepCompare}.`,
    );
  }

  const settings = deepCompare as DeepCompareSettings;

  if (!('compareThreshold' in settings) || settings.compareThreshold === undefined) {
    throw new TypeError(
      `Invalid \`deepCompare\` in config file ${configFilePath}: \`compareThreshold\` is required.`,
    );
  }

  if (
    typeof settings.compareThreshold !== 'number' ||
    settings.compareThreshold < 0 ||
    settings.compareThreshold > 1
  ) {
    throw new TypeError(
      `Invalid \`deepCompare.compareThreshold\` in config file ${configFilePath}: must be a number between 0 and 1, got: ${JSON.stringify(settings.compareThreshold)}.`,
    );
  }

  if (
    'diffAlgorithm' in settings &&
    settings.diffAlgorithm !== undefined &&
    (typeof settings.diffAlgorithm !== 'string' ||
      (settings.diffAlgorithm !== 'color-delta' &&
        settings.diffAlgorithm !== 'ssim'))
  ) {
    throw new TypeError(
      `Invalid \`deepCompare.diffAlgorithm\` in config file ${configFilePath}: must be "color-delta" or "ssim", got: ${JSON.stringify(settings.diffAlgorithm)}.`,
    );
  }

  if (
    'ignoreThreshold' in settings &&
    settings.ignoreThreshold !== undefined &&
    (typeof settings.ignoreThreshold !== 'number' ||
      settings.ignoreThreshold < 0 ||
      settings.ignoreThreshold > 1)
  ) {
    throw new TypeError(
      `Invalid \`deepCompare.ignoreThreshold\` in config file ${configFilePath}: must be a number between 0 and 1, got: ${JSON.stringify(settings.ignoreThreshold)}.`,
    );
  }

  if (
    'ignoreWhitespace' in settings &&
    settings.ignoreWhitespace !== undefined &&
    typeof settings.ignoreWhitespace !== 'boolean'
  ) {
    throw new TypeError(
      `Invalid \`deepCompare.ignoreWhitespace\` in config file ${configFilePath}: must be a boolean, got: ${JSON.stringify(settings.ignoreWhitespace)}.`,
    );
  }

  if (
    'applyBlur' in settings &&
    settings.applyBlur !== undefined &&
    typeof settings.applyBlur !== 'boolean'
  ) {
    throw new TypeError(
      `Invalid \`deepCompare.applyBlur\` in config file ${configFilePath}: must be a boolean, got: ${JSON.stringify(settings.applyBlur)}.`,
    );
  }
}

export async function loadConfigFile(
  configFilePath: string,
  environment?: Pick<EnvironmentResult, 'link' | 'ci'>,
  logger: Logger = console,
): Promise<ConfigWithDefaults> {
  try {
    const stats = await fs.promises.stat(configFilePath);
    if (!stats.isFile()) {
      throw new Error(`Happo config file path is not a file: ${configFilePath}`);
    }
  } catch (error) {
    if (error instanceof Error && 'code' in error && error.code === 'ENOENT') {
      throw new Error(`Happo config file could not be found: ${configFilePath}`, {
        cause: error,
      });
    }

    throw error;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: change this to unknown and add type assertions
  let config: any;
  try {
    config = (await import(pathToFileURL(configFilePath).href)).default;
  } catch (error) {
    if (
      error instanceof Error &&
      'code' in error &&
      error.code === 'ERR_UNKNOWN_FILE_EXTENSION'
    ) {
      // Older versions of Node don't support .ts files natively, so let's throw
      // a more helpful error message.
      const extension = path.extname(configFilePath);
      throw new TypeError(
        `Your Happo config file ${configFilePath} is using an extension that is not supported by this version of Node.js (${extension}). Please use a newer version of Node.js (22.18.0+, 23.6.0+, or 24+).`,
        { cause: error },
      );
    }

    throw error;
  }

  if (config === null) {
    throw new TypeError(
      `Your Happo config file ${configFilePath} must have a default export that is an object, got: null.`,
    );
  }

  if (typeof config !== 'object') {
    throw new TypeError(
      `Your Happo config file ${configFilePath} must have a default export that is an object, got: ${typeof config}.`,
    );
  }

  if (Array.isArray(config)) {
    throw new TypeError(
      `Your Happo config file ${configFilePath} must have a default export that is an object, got: array.`,
    );
  }

  // We read these in here so that they can be passed along to the child process
  // in e2e/wrapper.ts. This allows us to use pull-request authentication
  // without having to make an additional HTTP request.
  if (!config.apiKey && process.env.HAPPO_API_KEY) {
    config.apiKey = process.env.HAPPO_API_KEY;
  }
  if (!config.apiSecret && process.env.HAPPO_API_SECRET) {
    config.apiSecret = process.env.HAPPO_API_SECRET;
  }

  if (!config.apiKey || !config.apiSecret) {
    const missing = [
      config.apiKey ? null : 'apiKey',
      config.apiSecret ? null : 'apiSecret',
    ]
      .filter(Boolean)
      .map((key) => `\`${key}\``)
      .join(' and ');

    logger.log(
      `Missing ${missing} in Happo config. Attempting alternative authentication.`,
    );
    const fallbackApiToken = await getFallbackApiToken(
      config.endpoint || DEFAULT_ENDPOINT,
      environment,
      logger,
    );
    if (!fallbackApiToken) {
      throw new Error(
        `Missing ${missing} in your Happo config. Reference yours at https://happo.io/settings`,
      );
    }
    config.apiKey = fallbackApiToken.key;
    config.apiSecret = fallbackApiToken.secret;
  }

  if (!config.targets) {
    config.targets = {
      chrome: {
        type: 'chrome',
        viewport: '1024x768',
      },
    };
  }

  if (!config.integration) {
    config.integration = {
      type: 'storybook',
    };
  }

  const allTargets = Object.values(config.targets);
  for (const target of allTargets as Array<TargetWithDefaults>) {
    target.viewport = target.viewport || '1024x768';
    target.freezeAnimations = target.freezeAnimations || 'last-frame';
    target.prefersReducedMotion = target.prefersReducedMotion ?? true;
    target.allowPointerEvents = target.allowPointerEvents ?? true;
  }

  // Validate deepCompare settings if present
  if (config.deepCompare !== undefined && config.deepCompare !== null) {
    validateDeepCompareSettings(config.deepCompare, configFilePath);
    // Set default diffAlgorithm if not provided
    if (!config.deepCompare.diffAlgorithm) {
      config.deepCompare.diffAlgorithm = 'color-delta';
    }
  }

  if (
    config.failOnWaitForTimeout !== undefined &&
    typeof config.failOnWaitForTimeout !== 'boolean'
  ) {
    // Use `util.inspect` rather than `JSON.stringify` so that values which
    // can't be serialized (BigInts, circular objects, etc.) still produce
    // the intended "must be a boolean" validation error instead of an
    // unrelated serialization failure.
    throw new TypeError(
      `Invalid \`failOnWaitForTimeout\` in config file ${configFilePath}: must be a boolean, got: ${inspect(config.failOnWaitForTimeout)}.`,
    );
  }

  if (config.failOnWaitForTimeout === undefined) {
    config.failOnWaitForTimeout = true;
  }

  const configWithDefaults = {
    endpoint: DEFAULT_ENDPOINT,
    githubApiUrl: 'https://api.github.com',
    targets: allTargets,
    ...config,
  };

  return configWithDefaults;
}
